<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import { statusDistributionHidden, statusDistributionShown } from "@/analytics/events/other";
import { trackEvent } from "@/analytics/track";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import StatusDot from "@/components/ui/StatusDot.vue";
import { statusClassNames } from "@/model/baseTypes";
import { statusClassColors } from "@/model/colors";
import type { Iteration } from "@/model/session";
import type { StatusClassDistribution, StatusDistributionSource } from "@/model/status";
import { useZoomStore } from "@/store/zoom";

import SvgIcon from "../ui/SvgIcon/SvgIcon.vue";
import { emptyDistribution } from "./StatusDistribution";
import StatusDistribution from "./StatusDistribution.vue";
import DateBanner from "./internal/DateBanner.vue";
import { createBarChartData } from "./internal/StackedBarChart";
import StackedBarChart from "./internal/StackedBarChart.vue";
import type { StatusDistributionFilters } from "./internal/useStatusDistribution";

const sourceKeys: Record<StatusDistributionSource, string> = {
  iteration: /*$t*/ "statusDistribution.emptyIteration",
  objective: /*$t*/ "statusDistribution.emptyObjective",
  sticky: /*$t*/ "statusDistribution.emptySticky",
  team: /*$t*/ "statusDistribution.emptyTeam",
  art: /*$t*/ "statusDistribution.emptyArt",
};

interface Props {
  value?: StatusClassDistribution;
  sourceItemType: StatusDistributionSource;
  iteration?: Iteration;
  filters: StatusDistributionFilters;
  title: string;
}

const props = withDefaults(defineProps<Props>(), {
  value: emptyDistribution,
  iteration: undefined,
});

const { t } = useI18n();

const chartData = computed(() => {
  return {
    ...createBarChartData(props.value, (key, value) => ({
      name: t(statusClassNames[key]),
      points: value.points,
      value: value.value,
      status: key,
      color: statusClassColors[key],
    })),
    totalPoints: [...props.value.values()].reduce((sum, item) => sum + item.points, 0),
  };
});

const items = computed(() => [...chartData.value.items]);
const emptyMessage = computed(() => t(sourceKeys[props.sourceItemType]));

// Dropdown state for detailed status distribution
const dropdownRef = useTemplateRef("dropdown");
const isDropdownOpen = ref(false);

function onShow() {
  trackEvent(statusDistributionShown(props.sourceItemType));
}

function onHide() {
  trackEvent(statusDistributionHidden(props.sourceItemType));
}

// Prevent hover tooltip from showing when dropdown is visible
function shouldShowHoverTooltip(): false | void {
  if (isDropdownOpen.value) {
    return false;
  }
}

function onDropdownOpen() {
  isDropdownOpen.value = true;
}

function onDropdownClose() {
  isDropdownOpen.value = false;
}

// Close dropdown when zooming
const zoomStore = useZoomStore();
watch(
  () => zoomStore.zooming,
  (isZooming) => {
    if (isZooming) {
      dropdownRef.value?.close();
    }
  },
);
</script>

<template>
  <div class="status-distribution" data-testid="status-distribution">
    <DropdownMenu
      ref="dropdown"
      class="status-distribution-dropdown"
      width="auto"
      placement="bottom"
      :offset="{ top: 8, left: 0 }"
      stay-open-on-content-click
      @open="onDropdownOpen"
      @close="onDropdownClose"
    >
      <template #trigger>
        <BaseTooltip
          class="status-tooltip"
          position="bottom"
          tabindex="0"
          trigger-role="group"
          data-dropdown-trigger
          :aria-label="$t('general.statusClass')"
          :aria="{ content: 'owns' }"
          :on-show="shouldShowHoverTooltip"
          @shown="onShow"
          @hidden="onHide"
        >
          <template #content>
            <div class="status-distribution-content">
              <DateBanner v-if="iteration" :iteration class="date-banner" />
              <div v-if="chartData.total === 0" class="empty">
                {{ emptyMessage }}
              </div>
              <table v-else>
                <thead>
                  <tr>
                    <th>{{ $t("general.statusClass") }}</th>
                    <th>{{ $t("statusDistribution.storyPoints") }}</th>
                    <th>{{ $t("statusDistribution.workItems") }}</th>
                    <th>{{ $t("label.percentSign") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in items" :key="index">
                    <th scope="row">
                      <div>
                        <div class="status-dot-container">
                          <StatusDot radar-type :status-class="item.status" />
                        </div>
                        <span>{{ item.name }}</span>
                      </div>
                    </th>
                    <td>
                      {{
                        $t("general.partOfTotal", {
                          part: item.points,
                          total: chartData.totalPoints,
                        })
                      }}
                    </td>
                    <td>
                      {{ $t("general.partOfTotal", { part: item.value, total: chartData.total }) }}
                    </td>
                    <td>
                      {{
                        $t("general.percent", {
                          value: Math.round((item.value / chartData.total) * 100),
                        })
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="hint-text">
                <SvgIcon name="filled/info" width="16" height="16" />
                <span>{{ t("statusDistribution.clickForDetails") }}</span>
              </div>
            </div>
          </template>
          <StackedBarChart :value="chartData" />
        </BaseTooltip>
      </template>
      <StatusDistribution
        :filters
        :source="sourceItemType"
        :iteration
        :title
        :show-actions="sourceItemType !== 'objective'"
      />
    </DropdownMenu>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/font";
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.status-distribution {
  display: flex;
  height: 100%;
  cursor: pointer;

  .status-distribution-dropdown {
    display: flex;
    width: 100%;
    height: 100%;

    :deep(.trigger) {
      display: flex;
      width: 100%;
      height: 100%;
    }

    // BaseTooltip renders as a span, ensure it fills the container
    :deep(.base-tooltip) {
      display: flex;
      width: 100%;
      height: 100%;
    }
  }

  .status-tooltip {
    display: flex;
    width: 100%;
    height: 100%;

    // BaseTooltip renders as a span, ensure it fills the container
    :deep(.base-tooltip) {
      display: flex;
      width: 100%;
      height: 100%;
    }
  }

  &-content {
    padding: 6px 0;

    .date-banner {
      margin-bottom: 12px;
      background-color: colors.$palette-white-alpha-5;
    }

    .status-dot-container {
      width: 1.5em;
      height: 1.5em;
    }

    table {
      column-gap: 40px !important;
    }

    th:last-child,
    td:last-child {
      text-align: right !important;
    }

    /* stylelint-disable no-descending-specificity */
    td,
    th[scope="row"] {
      font-size: font.$size-normal;
      padding-bottom: 4px;

      &:not(:first-child) {
        text-align: right;
      }

      &:first-child {
        & > div {
          display: flex;
          align-items: center;
          gap: 6px;
        }
      }
    }

    td:not(:last-child),
    th:not(:last-child) {
      padding-right: 10px;
    }

    th:not([scope="row"]) {
      font-size: font.$size-small;
      font-weight: font.$weight-bold;
      color: colors.$text-subtle;
      padding-bottom: 8px;

      &:first-child {
        text-align: left;
      }
    }

    .empty {
      padding: 0.3em 1em;
      white-space: nowrap;
    }

    .hint-text {
      display: flex;
      gap: 4px;
      align-items: center;
      margin-top: 12px;

      @include typography.extra-small("bold");

      color: colors.$text-placeholder;
    }
  }
}
</style>
